import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { INotification } from '../../services/api/notificationService';
import { getFileById } from '../../services/api/fileService';
import { UserFile } from '../files/FilesOverviewPage';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import logo from '../../images/LogohvidFinal.png';

interface NotificationPreviewDialogProps {
    notification: INotification | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Custom footer action. Defaults to a "Close" button. */
    footerAction?: React.ReactNode;
}

const RICH_TEXT_CLASSES = cn(
    'min-w-0 w-full max-w-full break-words whitespace-normal [overflow-wrap:anywhere] text-sm leading-relaxed',
    '[&_*]:!min-w-0 [&_*]:!max-w-full [&_*]:[overflow-wrap:anywhere]',
    '[&_p]:mb-2 [&_p]:break-words [&_p:last-child]:mb-0',
    '[&_strong]:font-semibold',
    '[&_em]:italic',
    '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2',
    '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2',
    '[&_li]:mb-0.5 [&_li]:break-words',
    '[&_a]:break-all [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2',
    '[&_pre]:whitespace-pre-wrap [&_table]:w-full [&_table]:table-fixed',
    '[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mb-2',
    '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mb-1.5',
    '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mb-1',
    '[&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-muted-foreground',
    'leading-6 text-white/80 [font-family:Inter] [&_*]:![font-family:Inter] [&_a]:!text-white [&_blockquote]:!border-white/25 [&_blockquote]:!text-white/65'
);

const NotificationPreviewDialog: React.FC<NotificationPreviewDialogProps> = ({
    notification,
    open,
    onOpenChange,
    footerAction,
}) => {
    const [file, setFile] = useState<UserFile | null>(null);

    useEffect(() => {
        if (!notification || !open) {
            setFile(null);
            return;
        }
        if (notification.video_id) {
            getFileById(notification.video_id)
                .then(setFile)
                .catch(() => setFile(null));
        } else {
            setFile(null);
        }
    }, [notification, open]);

    if (!notification) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className={cn(
                    'flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-lg flex-col overflow-hidden rounded-lg border-white/10 p-5 text-white shadow-[0_24px_80px_rgba(0,0,0,0.45)] sm:max-h-[85vh] [&>button]:!right-2 [&>button]:!top-2 [&>button]:!rounded-md [&>button]:!p-1 [&>button]:!text-white/70 [&>button]:!opacity-100 [&>button]:hover:!bg-white/10 [&>button]:hover:!text-white',
                    file?.documentType === 'video' && 'sm:max-w-3xl'
                )}
                style={{
                    background:
                        'linear-gradient(135deg, #343434 0%, #2b2b2b 58%, #232323 100%)',
                }}
            >
                <DialogHeader className="shrink-0 text-left">
                    <img
                        src={logo}
                        alt="NordInsight"
                        className="mb-4 h-6 w-auto max-w-none self-start object-contain"
                    />
                    <DialogTitle className="break-words pr-8 text-xl font-semibold tracking-normal">
                        {notification.title || 'Notification'}
                    </DialogTitle>
                </DialogHeader>
                <div className="min-h-0 min-w-0 w-full max-w-full flex-1 space-y-3 overflow-x-hidden overflow-y-auto">
                    {notification.description && (
                        <div
                            className={RICH_TEXT_CLASSES}
                            dangerouslySetInnerHTML={{
                                __html: notification.description,
                            }}
                        />
                    )}
                    {file && (
                        <div className="rounded-md border bg-muted/30 overflow-hidden !border-white/10 !bg-black/20">
                            {file.documentType === 'video' && (
                                <video
                                    className="w-full max-h-96"
                                    controls
                                    autoPlay
                                    src={file.url}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            )}
                            {file.documentType === 'image' && (
                                <img
                                    src={file.url}
                                    alt={notification.title || 'Attachment'}
                                    className="w-full max-h-64 object-contain"
                                />
                            )}
                        </div>
                    )}
                </div>
                <div className="mt-1 flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-4">
                    <div>
                        {notification.action?.type === 'external_link' && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="gap-1.5 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
                                asChild
                            >
                                <a
                                    href={notification.action.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    {notification.action.label || 'Open link'}
                                </a>
                            </Button>
                        )}
                    </div>
                    {footerAction ?? (
                        <Button
                            variant="outline"
                            className="border-transparent bg-primary text-primary-foreground hover:bg-primary/90"
                            onClick={() => onOpenChange(false)}
                        >
                            Close
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default NotificationPreviewDialog;
